import * as dayjs from "dayjs";
import { v4 as uuidv4 } from 'uuid';
import { parseSQL } from "react-querybuilder";

/**
 * Normalize an operator string (from AI response or parseSQL) to the internal
 * react-querybuilder operator key expected by the QueryBuilder component.
 * Handles natural-language phrases, SQL keywords, and ILIKE variants.
 */
export const normalizeOperator = (op) => {
  if (!op) return 'contains';
  const map = {
    // Natural-language equality
    'equals':                  '=',
    'equal':                   '=',
    'is equal to':             '=',
    'is':                      '=',
    'not equal':               '!=',
    'not equals':              '!=',
    'is not equal to':         '!=',
    'is not':                  '!=',
    // SQL LIKE / ILIKE → contains
    'like':                    'contains',
    'ilike':                   'contains',
    'not like':                'doesNotContain',
    'not ilike':               'doesNotContain',
    // Natural-language contains
    'does not contain':        'doesNotContain',
    // Starts / begins with
    'starts with':             'beginsWith',
    'start with':              'beginsWith',
    'begins with':             'beginsWith',
    'begin with':              'beginsWith',
    'does not begin with':     'doesNotBeginWith',
    'does not start with':     'doesNotBeginWith',
    // Ends with
    'ends with':               'endsWith',
    'end with':                'endsWith',
    'does not end with':       'doesNotEndWith',
    // Comparisons
    'greater than':            '>',
    'more than':               '>',
    'greater than or equal':   '>=',
    'at least':                '>=',
    'less than':               '<',
    'fewer than':              '<',
    'less than or equal':      '<=',
    'at most':                 '<=',
    // Null checks
    'is null':                 'null',
    'is not null':             'notNull',
    // Set operators
    'not in':                  'notIn',
    'not between':             'notBetween',
  };
  return map[op.toLowerCase()] ?? op;
};

export const createChildFilterObject = (filter, value, operatorValue) => {
  return {
    [filter?.key]: [
      {
        field: filter?.column,
        value: value,
        operator: operatorValue,
        dataType: filter.queryBuilderJSON[0]?.dataType,
        friendlyName: filter.friendlyName,
        [filter?.key]: value,
        id: uuidv4(),
        key: filter?.key,
      },
    ],
  };
};

export const groupBy = (objectArray, property) => {
  return objectArray.reduce(function (acc, obj) {
    if (obj) {
      var key = obj[property];
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(obj);
      return acc;
    }
  }, {});
};

export const createGroup = (combinator = "and") => {
  return {
    rules: [],
    combinator: combinator,
  };
};

export const createRule = (field, value, operator = "=", dataType = "TEXT") => {
  return {
    field,
    value,
    operator,
    dataType,
    // not
  };
};

export const createRules = (array) => {
  if (array?.length > 0) {
    let queries = [];
    array.forEach((category, index) => {
      //creating group for each category
      queries.push(createGroup(category.combinator));

      recurseFilterModel(category.rows, queries[index]);
    });
    return queries;
  }

  return null;
};

export const recurseFilterModel = (array, queries) => {
  //console.log({ array })
  for (var i = 0; i < array?.length; i++) {
    //create a group for medication 1
    queries.rules.push(createGroup("and"));
    queries.rules[i].rules.push(
      createRule(array[i].field, array[i].value, array[i].operator, "TEXT")
    );
    //we created a group of medication names
    if (Array.isArray(array[i].rows) && array[i].rows?.length > 0) {
      // go through each grouped selected sub filters
      for (var j = 0; j < array[i].rows.length; j++) {
        const keys = Object.keys(array[i].rows[j]);
        //create group for sub child filter
        queries.rules[i].rules.push(createGroup("or"));
        for (var k = 0; k < array[i].rows[j][keys]?.length; k++) {
          //push each sub child filter to parent
          if (Object.keys(array[i].rows[j][keys][k]).length !== 0) {
            queries.rules[i].rules[j + 1].rules.push(
              createRule(
                array[i].rows[j][keys][k].field,
                array[i].rows[j][keys][k].value,
                array[i].rows[j][keys][k].operator
                  ? array[i].rows[j][keys][k].operator
                  : "=",
                array[i].rows[j][keys][k].dataType
                  ? array[i].rows[j][keys][k].dataType
                  : "TEXT",
                "or"
              )
            );
          }
        }
      }
    }
  }
};

export const columnValueDataType = (dataType) => {
  switch (dataType) {
    case "DATE":
      return dayjs().format("YYYY-MM-DD");
    case "TIME":
    case "TIMESTAMP_NTZ":
    case "TIMESTAMP_LTZ":
    case "TIMESTAMP_TZ":
      break;
    default:
      break;
  }
};

export const cleanupRowCount = (rowCount) => {
  let initialCount = 0;
  if (rowCount && typeof rowCount === "string") {
    if (rowCount?.includes(",")) {
      initialCount = rowCount.replace(/[!.,]/g, "");
    } else {
      initialCount = rowCount;
    }
    return Number(initialCount);
  }
};

export const simplifyHeader = (headerKey) => {
  headerKey = headerKey?.toLowerCase()?.replaceAll("_", " ");
  return headerKey
    ?.split(" ")
    ?.map(function (word) {
      return word.charAt(0)?.toUpperCase() + word.slice(1);
    })
    .join(" ");
};
export const createHeaders = (data) => {
  let headersArray = [];
  let arr;
  if (typeof data == "string") {
    arr = JSON.parse(data);
  } else {
    arr = data;
  }
  //check if its an array
  if (arr?.length > 0 && Array.isArray(arr)) {
    //get keys of first object
    var keys = Object.keys(arr[0]);
    //loop through and create the header object for each key and push to headersArray
    keys.forEach((key) => {
      let header = simplifyHeader(key);

      let obj = {
        field: key,
        headerName: header,
        minWidth: 150,
        flex: 1,
      };

      headersArray.push(obj);
    });
  }
  return headersArray;
};

export const generateRandom = () => {
  var length = 8,
    charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789",
    retVal = "";
  for (var i = 0, n = charset.length; i < length; ++i) {
    retVal += charset.charAt(Math.floor(Math.random() * n));
  }
  return retVal;
};

const generateId = (row) => {
  let id;
  for (const key in row) {
    if (Object.hasOwnProperty.call(row, key)) {
      if (typeof key === "number") {
        id += "-" + row[key];
      } else {
        id += row[key];
      }
    }
  }
  return id;
};

export const createRows = (data, page) => {
  let rowsArray = [];
  let arr;

  if (typeof data == "string") {
    arr = JSON.parse(data);
  } else {
    arr = data;
  }
  //check if its an array
  if (Array.isArray(arr)) {
    rowsArray = arr.map((obj) => ({ ...obj }));
    rowsArray.forEach((row, index) => {
      row.id = page + "1" + index;
    });
  }

  return rowsArray;
};

export const evaluateField = (field, props) => {
  if (typeof field === "function") {
    if (props) return field(props);
    return field();
  } else {
    return field;
  }
};

//iterate over the rules array to add dataType prop to each rule
export const rulesIterator = (arr, combinedFields) => {
  if (arr?.length < 1) return;

  for (var i = 0; i < arr?.length; i++) {
    if (Object.prototype.hasOwnProperty.call(arr[i], "dataType")) {
      break;
    }
    if (Object.prototype.hasOwnProperty.call(arr[i], "rules")) {
      rulesIterator(arr[i].rules);
    } else {
      let field = combinedFields.find(
        (item) => item.schemaTableColumnName === arr[i].field
      );
      if (field) {
        arr[i].dataType = field.dataType;
      } else {
        console.log("couldn't map dataType");
      }
    }
  }
  return arr;
};

export const populateDisplayFields = (filters, displayFields) => {
  let arr = [];
  filters.forEach((category) => {
    arr.push({
      field: category.column,
      label: category.category,
    });

    if (category?.rows?.length > 0) {
      category.rows.forEach((row) => {
        if (row?.rows?.length > 0) {
          row.rows.forEach((child) => {
            if (child) {
              for (var key in child) {
                if (Object.prototype.hasOwnProperty.call(child, key)) {
                  child[key].forEach((childFilter) => {
                    arr.push({
                      field: childFilter.field,
                      label: childFilter.key,
                      category: category.category,
                    });
                  });
                }
              }
            }
          });
        }
      });
    }
  });
  //create unique set of objects
  const combinedColumns = [...displayFields, ...arr];
  const uniqueColumns = [
    ...new Map(combinedColumns.map((v) => [JSON.stringify(v), v])).values(),
  ];
  return uniqueColumns;
};

//iterator to remove select options from rules
export const removeSelectOptionsIterator = (arr) => {
  if (arr.length < 1) return;

  for (var i = 0; i < arr.length; i++) {
    if (Object.prototype.hasOwnProperty.call(arr[i], "selectOptions")) {
      delete arr[i].selectOptions;
      break;
    }
    if (Object.prototype.hasOwnProperty.call(arr[i], "rules")) {
      rulesIterator(arr[i].rules);
    }
  }
  return arr;
};

// ─── SQL → filter parser ──────────────────────────────────────────────────────
// Converts a Cortex agent sql_statement into the filter array used by setFilters.
// Cortex Analyst SQL routinely nests the real conditions inside CTEs and
// IN (SELECT ...) subqueries, so we extract every WHERE clause in the statement
// and hand each one to react-querybuilder's parseSQL, then map the resulting
// rules onto metadata.queryItems. parseSQL silently drops IN (SELECT ...)
// predicates, so the outer clause and the subquery's own WHERE never duplicate.

const WHERE_TERMINATOR = /^(GROUP\s+BY|ORDER\s+BY|HAVING|LIMIT|QUALIFY|UNION|WINDOW|FETCH|OFFSET)\b/i;

// Extract the body of every WHERE clause in the statement, tracking string
// literals and paren depth so clauses inside CTEs and subqueries are each
// captured on their own.
const extractWhereClauses = (sql) => {
  const clauses = [];
  const upper = sql.toUpperCase();
  let searchFrom = 0;

  while (searchFrom < sql.length) {
    const idx = upper.indexOf('WHERE', searchFrom);
    if (idx === -1) break;
    searchFrom = idx + 5;

    // Keyword must stand alone (not part of an identifier)
    const before = idx === 0 ? ' ' : sql[idx - 1];
    const after = sql[idx + 5] ?? ' ';
    if (/[\w.]/.test(before) || /\w/.test(after)) continue;

    let body = '';
    let depth = 0;
    let inStr = false;
    for (let j = idx + 5; j < sql.length; j++) {
      const ch = sql[j];
      if (inStr) {
        body += ch;
        if (ch === "'") {
          if (sql[j + 1] === "'") { body += "'"; j++; } // escaped quote
          else inStr = false;
        }
        continue;
      }
      if (ch === "'") { inStr = true; body += ch; continue; }
      if (ch === '(') { depth++; body += ch; continue; }
      if (ch === ')') {
        if (depth === 0) break; // closes the subquery/CTE containing this WHERE
        depth--;
        body += ch;
        continue;
      }
      if (depth === 0 && ch === ';') break;
      if (depth === 0 && /[\s)]/.test(sql[j - 1]) && WHERE_TERMINATOR.test(sql.slice(j))) break;
      body += ch;
    }
    if (body.trim()) clauses.push(body.trim());
  }
  return clauses;
};

// Depth-first flatten of a parseSQL rule group into leaf rules.
const flattenRuleGroup = (group, out = []) => {
  (group?.rules || []).forEach((rule) => {
    if (rule.rules) flattenRuleGroup(rule, out);
    else if (rule.field) out.push(rule);
  });
  return out;
};

// Operators whose rows can be OR'd together within a filter group. Everything
// else (exclusions, range bounds, null checks) must be AND'd — e.g.
// "AGE >= 18 OR AGE <= 35" or "ICD != 'A' OR ICD != 'B'" would match everyone.
const OR_JOINED_OPERATORS = new Set(['=', 'contains', 'beginsWith', 'endsWith']);

// Expand set/range operators into the simple rows the filter UI understands.
// `join` overrides the combinator the row is grouped under.
const expandRule = (rule) => {
  const value = rule.value;
  const asArray = Array.isArray(value) ? value : [value];
  switch (rule.operator) {
    case 'in':
      return asArray.map((v) => ({ value: v, operator: '=' }));
    case 'notIn':
      return asArray.map((v) => ({ value: v, operator: '!=' }));
    case 'between':
      return [
        { value: asArray[0], operator: '>=' },
        { value: asArray[1], operator: '<=' },
      ];
    case 'notBetween':
      return [
        { value: asArray[0], operator: '<', join: 'or' },
        { value: asArray[1], operator: '>', join: 'or' },
      ];
    default:
      return [{ value, operator: rule.operator }];
  }
};

/**
 * Parse a Cortex agent sql_statement into the filter array used by setFilters/createRules.
 * Handles flat conditions, OR groups, IN/NOT IN, BETWEEN, LIKE/ILIKE wildcards,
 * IS (NOT) NULL, and conditions nested inside CTEs or IN (SELECT ...) subqueries.
 * Column names are matched against metadata.queryItems (including childFilters)
 * by full path or bare column name, ignoring table aliases.
 */
export const parseSqlToFilters = (sql, metadata) => {
  if (!sql || !metadata?.queryItems) return [];

  // parseSQL's grammar has no ILIKE — Snowflake's case-insensitive LIKE is
  // equivalent for filter-building purposes.
  const normalized = sql
    .replace(/\s+/g, ' ')
    .replace(/\bNOT\s+ILIKE\b/gi, 'NOT LIKE')
    .replace(/\bILIKE\b/gi, 'LIKE')
    .trim();

  // Build column lookup (column name → queryItem).
  // Metadata columns are stored as full paths e.g. "PSYCH.PATIENT_SUMMARY.GENDER".
  // Cortex Analyst SQL typically uses the bare column or alias.column form,
  // so index by both the full path AND the bare column name.
  const columnMap = {};
  const indexItems = (items) => {
    if (!Array.isArray(items)) return;
    items.forEach((item) => {
      if (item.column) {
        columnMap[item.column.toUpperCase()] = item;
        const shortName = item.column.split('.').pop().toUpperCase();
        if (shortName && !columnMap[shortName]) {
          columnMap[shortName] = item;
        }
      }
      if (item.childFilters) indexItems(item.childFilters);
    });
  };
  indexItems(metadata.queryItems);

  const leafRules = [];
  extractWhereClauses(normalized).forEach((clause) => {
    try {
      const parsed = parseSQL(`SELECT * FROM t WHERE ${clause}`, { listsAsArrays: true });
      flattenRuleGroup(parsed, leafRules);
    } catch (err) {
      console.warn('parseSqlToFilters: skipping unparseable WHERE clause:', clause);
    }
  });

  const filterMap = {};
  const seenRows = new Set();

  leafRules.forEach((rule) => {
    const field = String(rule.field || '');
    const bareName = (field.includes('.') ? field.split('.').pop() : field).toUpperCase();
    const queryItem = columnMap[field.toUpperCase()] || columnMap[bareName];
    if (!queryItem) return;

    expandRule(rule).forEach(({ value, operator, join }) => {
      // Same condition can appear twice when the agent repeats it across queries
      const rowKey = `${queryItem.column}|${operator}|${value}`;
      if (seenRows.has(rowKey)) return;
      seenRows.add(rowKey);

      const combinator = join || (OR_JOINED_OPERATORS.has(operator) ? 'or' : 'and');
      const groupKey = `${queryItem.column}::${combinator}`;
      if (!filterMap[groupKey]) {
        filterMap[groupKey] = {
          id: uuidv4(),
          category: queryItem.category,
          friendlyName: queryItem.friendlyName,
          column: queryItem.column,
          key: queryItem.key,
          isChildFilterNumeric: queryItem.isChildFilterNumeric || false,
          combinator,
          rows: [],
        };
      }
      filterMap[groupKey].rows.push({
        id: uuidv4(),
        field: queryItem.column,
        value,
        operator,
        friendlyName: queryItem.friendlyName,
        key: queryItem.key,
        rows: [],
      });
    });
  });

  return Object.values(filterMap);
};

// ─────────────────────────────────────────────────────────────────────────────

export const handleBlobDownload = (blob, fileName) => {
  if (navigator.msSaveBlob) {
    // IE 10+
    navigator.msSaveBlob(blob, fileName);
  } else {
    var link = document.createElement("a");
    if (link.download !== undefined) {
      // feature detection
      // Browsers that support HTML5 download attribute
      var url = window.URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", fileName);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};
export const handleFileDownload = (
  data,
  fileName,
  blobProps = { type: "text/csv;charset=utf-8;" }
) => {
  const csvData = objectToCsv(data);
  let blob = new Blob([csvData], blobProps);
  if (navigator.msSaveBlob) {
    // IE 10+
    navigator.msSaveBlob(blob, fileName);
  } else {
    var link = document.createElement("a");
    if (link.download !== undefined) {
      // feature detection
      // Browsers that support HTML5 download attribute
      var url = window.URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", fileName);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};

const objectToCsv = function (data) {
  const csvRows = [];

  /* Get headers as every csv data format 
    has header (head means column name)
    so objects key is nothing but column name 
    for csv data using Object.key() function.
    We fetch key of object as column name for 
    csv */
  const headers = Object.keys(data[0]);

  /* Using push() method we push fetched 
       data into csvRows[] array */
  csvRows.push(headers.join(","));

  // Loop to get value of each objects key
  for (const row of data) {
    const values = headers.map((header) => {
      const val = row[header];
      return `"${val}"`;
    });

    // To add, sepearater between each value
    csvRows.push(values.join(","));
  }

  /* To add new line for each objects values
       and this return statement array csvRows
       to this function.*/
  return csvRows.join("\n");
};
